/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Button, ButtonProps} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor, dlsLayout} from '@ion/dls/src/theme/theme';

export interface FloatingIconButtonProps extends ButtonProps {
  children: JSX.Element;
  /**
   * If `true`, Button will be in selected mode.
   */
  isSelected?: boolean;
  round?: boolean;
  backgroundColor?: string;
}
export interface FloatingIconButtonGroupProps {
  /**
   * Content of the `FloatingIconButtonGroup`.
   */
  children: JSX.Element;
  /**
   * If `true`, Align the icons in vertical.
   *
   * By default, Icons are aligned in horizontal,
   */
  isVertical?: boolean;
  /**
   * If true, the icon will be displayed without a shadow and background
   */
  noShadow?: boolean;
  style?: GenericObject;
}

type StyleProps = {
  round: boolean;
  backgroundColor: string;
};

const useStyles = makeStyles(theme => {
  const iconSize = dlsAlt('components', 'btn', 'floatingIconSize');
  return ({
    root : {
      background   : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('white'),
      height       : iconSize,
      width        : iconSize,
      minWidth     : iconSize,
      padding      : dlsLayout('spacing'),
      borderRadius : ({round}: StyleProps) => round ? '50% !important' : 1,
      boxShadow    : 'none',
      '&:active'   : {
        background : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('grey1')
      },
      '&:hover' : {
        background : ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('paleBlue'),
        boxShadow  : 'none'
      },
      '&$disabled' : {
        background : dlsColor('white'),
        boxShadow  : 'none'
      }
    },
    disabled : {}
  });
});

const FloatingIconButtonComponent = ({
  children,
  isSelected = false,
  backgroundColor,
  ...props
}: FloatingIconButtonProps) => {
  const classes = useStyles({round: props?.round, backgroundColor});
  return (
    <Button variant='contained'
      classes={{
        root     : classes.root,
        disabled : classes.disabled
      }}
      style={{...(isSelected && {backgroundColor: dlsColor('grey1')})}}
      {...props}
      color='primary'>
      {React.cloneElement(children, {
        color : props.color ? props.color
          : props.disabled
            ? dlsColor('grey4')
            : isSelected
              ? dlsColor('grey6')
              : props?.color || dlsColor('caBlue')
      })}
    </Button>
  );
};

/**
 *
 * @FloatingIcon
 *
 * Floating icon buttons appear above the maps, pdf or reports, and are in the sub
 * navigation below the secondary header. In instances with 5 or more buttons, they
 * can be grouped together in one container.
 *
 * ```typescript
 *
 * import React from 'react';
 * import { FloatingIconButtonGroup } from '@ion/dls/src/components/buttons/';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <FloatingIconButtonGroup isVertical>
 *        <>
 *          <FloatingIconButtonGroup.FloatingIconButton
 *            onClick={() => {
 *              alert('MapZoomInIcon clicked');
 *            }}>
 *            <MapZoomInIcon size={IconSize.large}
 *              color={dlsColor('caBlue')} />
 *          </FloatingIconButtonGroup.FloatingIconButton>
 *          <FloatingIconButtonGroup.FloatingIconButton
 *            onClick={() => {
 *              alert('MapZoomOutIcon clicked');
 *            }}>
 *            <MapZoomOutIcon size={IconSize.large}
 *              color={dlsColor('caBlue')} />
 *          </FloatingIconButtonGroup.FloatingIconButton>
 *        </>
 *      </FloatingIconButtonGroup>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const FloatingIconButtonGroup = ({
  children,
  isVertical,
  noShadow,
  style = {}
}: FloatingIconButtonGroupProps) => {
  return (
    <div style={{
      boxShadow     : noShadow ? 'none' : '0px 2px 10px rgba(0, 0, 0, 0.2)',
      display       : 'flex',
      flexDirection : isVertical ? 'column' : 'row',
      ...style
    }}>
      {children}
    </div>
  );
};
FloatingIconButtonGroup.FloatingIconButton = FloatingIconButtonComponent;

export {FloatingIconButtonGroup};
